import React, { useState } from 'react';
import { Sparkles, Calendar, BookOpen, Users } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { showSuccessToast, showInfoToast, showErrorToast } from '../utils/toastConfig';

const CursosPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) {
      showErrorToast(toast, 'Por favor completa tu nombre y email.');
      return;
    }
    setLoading(true);
    try {
      await api.post('users/subscribers/', { email, name });
      showSuccessToast(toast, '¡Perfecto! Te notificaremos cuando lancemos los cursos.');
      setEmail('');
      setName('');
    } catch (err: any) {
      if (err.response?.status === 400) {
        showInfoToast(toast, 'Ya estás en nuestra lista de espera');
      } else {
        showErrorToast(toast, 'No pudimos procesar tu solicitud. Inténtalo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen pt-40 pb-20 px-4" style={{ backgroundColor: '#FFFBF7' }}>
      <div className="max-w-4xl mx-auto">
        {/* Icono y badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#B4897F]/10 rounded-full mb-6">
            <BookOpen className="w-10 h-10 text-[#B4897F]" />
          </div>
          <div className="inline-block bg-[#B4897F]/10 text-[#B4897F] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Próximamente
          </div>
        </div>

        {/* Título principal */}
        <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 text-center mb-6">
          Cursos Profesionales
        </h1>
        
        <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          Estamos preparando experiencias de aprendizaje únicas para elevar tus habilidades profesionales
        </p>

        {/* Tarjetas de características */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-[#B4897F]/30 transition-all duration-300">
            <div className="w-12 h-12 bg-[#B4897F]/10 rounded-lg flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-[#B4897F]" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Contenido Exclusivo</h3>
            <p className="text-gray-600 text-sm">
              Aprende técnicas avanzadas con profesionales de la industria
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-[#B4897F]/30 transition-all duration-300">
            <div className="w-12 h-12 bg-[#B4897F]/10 rounded-lg flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-[#B4897F]" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Clases Prácticas</h3>
            <p className="text-gray-600 text-sm">
              Grupos reducidos para una atención personalizada
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-[#B4897F]/30 transition-all duration-300">
            <div className="w-12 h-12 bg-[#B4897F]/10 rounded-lg flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-[#B4897F]" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Horarios Flexibles</h3>
            <p className="text-gray-600 text-sm">
              Opciones de mañana, tarde y fines de semana
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-[#B4897F]/10 to-[#B4897F]/5 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
            ¿Te interesa ser el primero en enterarte?
          </h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Déjanos tu correo y te notificaremos cuando lancemos nuestros primeros cursos
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-md mx-auto">
            <input
              type="text"
              placeholder="Tu nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
            />
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              title="Por favor ingresa un email válido (ej: nombre@ejemplo.com)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
            />
            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-[#B4897F] text-white px-8 py-3 font-semibold hover:bg-[#A67A6F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Enviando...' : 'Notificarme'}
            </button>
          </form>
        </div>

        {/* Mensaje adicional */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            Mientras tanto, puedes{' '}
            <a href="/agendamiento" className="text-[#B4897F] hover:underline font-semibold">
              agendar una cita
            </a>
            {' '}o{' '}
            <a href="/products" className="text-[#B4897F] hover:underline font-semibold">
              explorar nuestra tienda
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CursosPage;
